// Shared visitor geolocation helper.
//
// Wraps Shopify's own Markets geolocation endpoint
// (/browsing_context_suggestions.json) so every script that needs the
// visitor's detected country/language shares one implementation instead of
// each rolling its own fetch (previously global-product-navigation.js used
// this endpoint while geo-link-redirect.liquid called a third-party IP
// lookup service — now both use this).
//
// Loaded as a blocking <script> in <head> (see theme.liquid) so it's
// available synchronously to inline snippet scripts that run during body
// parsing, as well as to deferred assets like global-product-navigation.js.
window.VivoGeoDetect = (function () {
  'use strict';

  // Shared with global-product-navigation.js (which forwards Pebble
  // product-page visitors from a regional store to vivolife.com, tagging
  // the redirect URL with ?origin=<region>) and geo-link-redirect.liquid
  // (which needs to resolve that same ?origin=<region> back to a domain
  // synchronously, before global-product-navigation.js's own deferred
  // script has had a chance to run and cache it in sessionStorage).
  var storeMap = {
    UK: 'https://www.vivolife.co.uk',
    US: 'https://www.vivolife.com',
    EU: 'https://eu.vivolife.com',
    DE: 'https://www.vivolife.de',
    FR: 'https://fr.vivolife.com',
    IT: 'https://it.vivolife.com'
  };

  // Canonical EU-member-state ISO-2 list (27 members) — shared source of
  // truth for any "is this visitor in the EU" check (e.g. the footer's
  // EU-only "Right of Withdrawal" link). Deliberately NOT the broader
  // "geographic Europe" list used elsewhere for language/market routing
  // (that one wrongly includes GB/CH/NO/etc for this purpose).
  var EU_MEMBER_STATE_ISOS = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
    'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
    'SI', 'ES', 'SE'
  ];

  // ---------------------------------------------------------------
  // Routing rules shared by geo-link-redirect.js and
  // global-product-navigation.js - keep them here so the two can't
  // drift apart again (they previously disagreed on e.g. BE/LU).
  // ---------------------------------------------------------------

  // FR/DE/IT keep their own storefronts; every other EU member state,
  // plus NO/CH/AE, belongs to the EU market. Anywhere unlisted defaults
  // to UK.
  var COUNTRY_REGION_OVERRIDES = { US: 'US', GB: 'UK', IE: 'UK', FR: 'FR', DE: 'DE', IT: 'IT' };
  var EU_MARKET_EXTRA_ISOS = ['NO', 'CH', 'AE'];
  var DEFAULT_REGION = 'UK';

  // vivolife.com's Shopify Markets locale per store region. US is the
  // un-prefixed root.
  var REGION_LOCALE_MAP = { US: '', UK: 'en-uk', GB: 'en-uk', EU: 'en-eu', FR: 'fr-eu', DE: 'de-eu', IT: 'it-eu' };
  var REGION_LANGUAGE_MAP = { FR: 'fr', DE: 'de', IT: 'it' };

  // URL locale segment -> store region. en-ch/en-no/en-ae are legacy
  // prefixes from before those countries were folded into en-eu.
  var LOCALE_REGION_MAP = {
    'en-us': 'US',
    'en-uk': 'UK',
    'en-gb': 'UK',
    'en-eu': 'EU',
    'en-ch': 'EU',
    'en-no': 'EU',
    'en-ae': 'EU',
    'fr': 'FR',
    'fr-eu': 'FR',
    'fr-fr': 'FR',
    'de': 'DE',
    'de-eu': 'DE',
    'de-de': 'DE',
    'it': 'IT',
    'it-eu': 'IT',
    'it-it': 'IT'
  };

  // ---------------------------------------------------------------
  // Region-only legal links in the footer + mobile menu (FooterMain.vue,
  // NavigationMenu.vue; see ai_memory/COMPLIANCE.md). Each takes
  // detectCountryCode()'s result; null (detection failed) errs towards
  // showing the link.
  // ---------------------------------------------------------------

  // The EU right of withdrawal also applies in the EEA. Not the same list
  // as EU_MARKET_EXTRA_ISOS (store routing).
  var EEA_EXTRA_ISOS = ['IS', 'LI', 'NO'];
  var EU_MARKET_REGIONS = ['EU', 'FR', 'DE', 'IT'];

  // The market this page sells into (/en-eu, /de-eu etc. on vivolife.com, or
  // the storefront's own host), whatever the visitor's IP.
  function getPageRegion() {
    var segment = window.location.pathname.split('/')[1] || '';
    return getRegionForLocale(segment) || getRegionForHostname(window.location.hostname);
  }

  function showWithdrawalLink(countryCode) {
    var code = normalizeCountryCode(countryCode);
    return !code || EU_MEMBER_STATE_ISOS.indexOf(code) !== -1 || EEA_EXTRA_ISOS.indexOf(code) !== -1 || EU_MARKET_REGIONS.indexOf(getPageRegion()) !== -1;
  }

  // German "cancel contracts here" button (BGB 312k): German consumers.
  function showGermanCancelLink(countryCode) {
    var code = normalizeCountryCode(countryCode);
    return !code || code === 'DE' || getPageRegion() === 'DE';
  }

  // US state privacy laws ("do not sell or share").
  function showUsPrivacyLink(countryCode) {
    var code = normalizeCountryCode(countryCode);
    return !code || code === 'US';
  }

  // Everywhere the cookie banner is shown, i.e. everywhere but the US.
  function showCookiePreferencesLink(countryCode) {
    return normalizeCountryCode(countryCode) !== 'US';
  }

  // Pebble product family - only sells on vivolife.com. Keep in sync with
  // the handle list in snippets_original/global-product-navigation.liquid.
  var PEBBLE_HANDLES = ['pebble', 'pebble-refill', 'pebble-case'];

  var MANUAL_KEY = 'vivo_manual_market';
  var MANUAL_PREFERENCE_KEY = 'vivo_manual_market_preference';

  function normalizeCountryCode(countryCode) {
    return String(countryCode || '').trim().toUpperCase();
  }

  // '' only when there's no country to go on (e.g. detection failed).
  function getRegionForCountry(countryCode) {
    var code = normalizeCountryCode(countryCode);
    if (!code) return '';
    if (COUNTRY_REGION_OVERRIDES[code]) return COUNTRY_REGION_OVERRIDES[code];
    if (EU_MEMBER_STATE_ISOS.indexOf(code) !== -1 || EU_MARKET_EXTRA_ISOS.indexOf(code) !== -1) return 'EU';
    return DEFAULT_REGION;
  }

  function getLocaleForRegion(region) {
    return REGION_LOCALE_MAP[normalizeCountryCode(region)] || '';
  }

  // null when there's no country; '' means the US root.
  function getLocaleForCountry(countryCode) {
    var region = getRegionForCountry(countryCode);
    return region ? getLocaleForRegion(region) : null;
  }

  // The country/language pair to submit to Shopify's /localization form.
  // Countries that only land in UK by default (AU, CA, ...) are submitted
  // as GB so Shopify actually puts them in the UK market.
  function getLocalizationForCountry(countryCode) {
    var code = normalizeCountryCode(countryCode);
    var region = getRegionForCountry(code);
    if (!region) return null;
    return {
      countryCode: region === 'UK' && !COUNTRY_REGION_OVERRIDES[code] ? 'GB' : code,
      languageCode: REGION_LANGUAGE_MAP[region] || 'en'
    };
  }

  function getRegionForLocale(locale) {
    return LOCALE_REGION_MAP[String(locale || '').replace(/_/g, '-').trim().toLowerCase()] || '';
  }

  function stripHost(value) {
    return String(value || '').trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/.*$/, '').toLowerCase();
  }

  function getRegionForHostname(hostname) {
    var host = stripHost(hostname);
    if (!host) return '';
    for (var region in storeMap) {
      if (Object.prototype.hasOwnProperty.call(storeMap, region) && stripHost(storeMap[region]) === host) return region;
    }
    return '';
  }

  function hasManualSelection() {
    try { return localStorage.getItem(MANUAL_KEY) === '1'; } catch (e) { return false; }
  }

  function getManualPreference() {
    try {
      var raw = localStorage.getItem(MANUAL_PREFERENCE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return {
        countryCode: normalizeCountryCode(parsed.countryCode),
        languageCode: String(parsed.languageCode || '').trim().toLowerCase()
      };
    } catch (e) {
      return null;
    }
  }

  var contextPromise = null;

  // Memoized so multiple callers on the same page (e.g. the header's
  // geo-link-redirect snippet and global-product-navigation.js) share one
  // request instead of firing it twice.
  function fetchBrowsingContext() {
    if (!contextPromise) {
      // no-store: this reflects the visitor's current IP, which can change
      // session to session (e.g. VPN) - a browser-cached response here would
      // silently keep reporting a stale country.
      contextPromise = fetch('/browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true', { cache: 'no-store' })
        .then(function (res) { return res.json(); })
        .then(function (data) { return (data && data.detected_values) || null; })
        .catch(function () { return null; });
    }
    return contextPromise;
  }

  function detectCountryCode() {
    return fetchBrowsingContext().then(function (detected) {
      return detected && detected.country && detected.country.handle
        ? detected.country.handle.toUpperCase()
        : null;
    });
  }

  return {
    storeMap: storeMap,
    EU_MEMBER_STATE_ISOS: EU_MEMBER_STATE_ISOS,
    PEBBLE_HANDLES: PEBBLE_HANDLES,
    MANUAL_KEY: MANUAL_KEY,
    MANUAL_PREFERENCE_KEY: MANUAL_PREFERENCE_KEY,
    fetchBrowsingContext: fetchBrowsingContext,
    detectCountryCode: detectCountryCode,
    getRegionForCountry: getRegionForCountry,
    getLocaleForRegion: getLocaleForRegion,
    getLocaleForCountry: getLocaleForCountry,
    getLocalizationForCountry: getLocalizationForCountry,
    getRegionForLocale: getRegionForLocale,
    getRegionForHostname: getRegionForHostname,
    hasManualSelection: hasManualSelection,
    getManualPreference: getManualPreference,
    showWithdrawalLink: showWithdrawalLink,
    showGermanCancelLink: showGermanCancelLink,
    showUsPrivacyLink: showUsPrivacyLink,
    showCookiePreferencesLink: showCookiePreferencesLink
  };
})();
